import { useCallback, useState } from 'react'
import { message } from 'antd'

import { AxiosError } from 'axios'
import { EAReportColumn } from 'Components/UI/EAReport/Table/utils/appendColumnPropertiesByType'

import api from 'Services/api'

import adjustReportData from './utils/adjustReportData'

type AnyColumn = EAReportColumn<Record<string, unknown>>

interface UseReportDataParams<T extends object> {
  columns: EAReportColumn<T>[]
  url: string
  dataPath?: string | null
}

interface ReportDataRequest<T> {
  data: T | T[]
  isLoading: boolean
}

interface UseReportDataReturn<T extends object> {
  reportDataRequest: ReportDataRequest<T>
  fetchReportData: (filtros: object, urlSuffix?: string) => void
  clearReportDataRequest: () => void
}

const joinUrl = (base: string, extra?: string): string => {
  if (!extra) return base
  const normalizedBase = base.replace(/\/$/, '')
  const normalizedExtra = extra.replace(/\/$/, '')
  return `${normalizedBase}/${normalizedExtra}/`
}

const useReportData = <T extends object>({
  columns,
  url,
  dataPath = null,
}: UseReportDataParams<T>): UseReportDataReturn<T> => {
  const [data, setData] = useState<T | T[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const fetchReportData = useCallback(
    (filtros: object, urlSuffix?: string) => {
      setIsLoading(true)
      const finalUrl = joinUrl(url, urlSuffix)

      api
        .post<Record<string, unknown>>(finalUrl, filtros)
        .then((response) => response.data)
        .then((reportData) => {
          if (dataPath) {
            const nested = reportData[dataPath] as T[]
            const cols = columns as unknown as AnyColumn[]
            return {
              ...reportData,
              [dataPath]: adjustReportData(cols, nested as unknown as Record<string, unknown>[]),
            } as unknown as T
          }
          const cols = columns as unknown as AnyColumn[]
          return adjustReportData(cols, reportData as unknown as Record<string, unknown>[]) as unknown as T
        })
        .then(setData)
        .catch((error: unknown) => {
          console.log(error)
          const err = error as AxiosError
          if (err.response?.status === 403) message.error('Usuário não possui permissão para filtrar relatorio.', 5)
          else message.error('Erro ao tentar obter os dados do relatório.')
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [dataPath, url, columns],
  )

  const clearReportDataRequest = useCallback(() => {
    setData([])
    setIsLoading(false)
  }, [])

  return { reportDataRequest: { data, isLoading }, fetchReportData, clearReportDataRequest }
}

export default useReportData
